import { useCallback, useEffect, useRef, useState } from 'react';

import type { PlatformType } from '../utils/platformGrouping';

type UseScrollResult = ModelHookResult<
	{
		canScrollLeft: boolean;
		canScrollRight: boolean;
		scrollContainerRef: React.RefObject<HTMLDivElement>;
	},
	{
		handleScroll: (direction: 'left' | 'right') => void;
	}
>;

export function useScroll(selectedPlatform: PlatformType | undefined): UseScrollResult {
	// Scroll container management
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// Function to update scroll button states
	const updateScrollState = () => {
		const container = scrollContainerRef.current;
		if (!container) return;

		setCanScrollLeft(container.scrollLeft > 0);
		setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
	};

	// Update scroll state when content or platform changes
	useEffect(() => {
		updateScrollState();
		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener('scroll', updateScrollState);
			window.addEventListener('resize', updateScrollState);
			return () => {
				container.removeEventListener('scroll', updateScrollState);
				window.removeEventListener('resize', updateScrollState);
			};
		}
		return undefined;
	}, [selectedPlatform]);

	// Scroll handlers
	const handleScroll = useCallback((direction: 'left' | 'right') => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scrollAmount = 300; // Scroll by this many pixels
		const newScrollLeft = container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
		container.scrollTo({
			left: newScrollLeft,
			behavior: 'smooth',
		});
	}, []);

	return {
		data: {
			canScrollLeft,
			canScrollRight,
			scrollContainerRef,
		},
		actions: {
			handleScroll,
		},
	};
}
